import { useState, useEffect } from 'react';
import { Bell, Plus, X, Check, Trash2, Calendar, Rocket, Music, Gift, Upload } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// File upload helper
const uploadFile = async (file, category) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_URL}/upload?category=${category}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.url;
};

export default function AdminPresaves({ presaves, onRefresh }) {
  const [editingPresave, setEditingPresave] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [uploading, setUploading] = useState({ cover: false, bonus: false });
  const [tracks, setTracks] = useState([]);
  const [newPresave, setNewPresave] = useState({
    title: '', cover_url: '', description: '', release_date: '', spotify_url: '', apple_music_url: '', audio_url: '',
    bonus_content_url: '', bonus_content_type: '', bonus_description: '',
    preorder_enabled: false, preorder_price: 1.00, preorder_track_id: ''
  });

  // Load tracks once so admin can link a pre-order to an existing track for ownership grant
  useEffect(() => {
    axios.get(`${API_URL}/tracks`).then(res => setTracks(res.data || [])).catch(() => {});
  }, []);

  // Handle cover image upload
  const handleCoverUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, cover: true }));
    try {
      const url = await uploadFile(file, 'image');
      if (isEditing) {
        setEditingPresave(prev => ({ ...prev, cover_url: url }));
      } else {
        setNewPresave(prev => ({ ...prev, cover_url: url }));
      }
      toast.success('Cover uploaded!');
    } catch (error) {
      toast.error('Failed to upload cover');
    } finally {
      setUploading(prev => ({ ...prev, cover: false }));
    }
  };

  // Handle bonus content upload
  const handleBonusUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, bonus: true }));
    try {
      const url = await uploadFile(file, 'audio');
      if (isEditing) {
        setEditingPresave(prev => ({ ...prev, bonus_content_url: url }));
      } else {
        setNewPresave(prev => ({ ...prev, bonus_content_url: url }));
      }
      toast.success('Bonus content uploaded!');
    } catch (error) {
      toast.error('Failed to upload bonus content');
    } finally {
      setUploading(prev => ({ ...prev, bonus: false }));
    }
  };

  const createPresave = async () => {
    if (!newPresave.title || !newPresave.release_date) {
      toast.error('Title and release date are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/presaves`, newPresave);
      toast.success('Presave created!');
      setShowCreate(false);
      setNewPresave({ title: '', cover_url: '', description: '', release_date: '', spotify_url: '', apple_music_url: '', audio_url: '', bonus_content_url: '', bonus_content_type: '', bonus_description: '', preorder_enabled: false, preorder_price: 1.00, preorder_track_id: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create presave');
    }
  };

  const savePresave = async (presave) => {
    try {
      await axios.put(`${API_URL}/admin/presaves/${presave.id}`, {
        title: presave.title,
        cover_url: presave.cover_url,
        description: presave.description,
        release_date: presave.release_date,
        spotify_url: presave.spotify_url,
        apple_music_url: presave.apple_music_url,
        audio_url: presave.audio_url,
        bonus_content_url: presave.bonus_content_url,
        bonus_content_type: presave.bonus_content_type,
        bonus_description: presave.bonus_description,
        preorder_enabled: !!presave.preorder_enabled,
        preorder_price: parseFloat(presave.preorder_price) || 1.00,
        preorder_track_id: presave.preorder_track_id || null
      });
      toast.success('Presave updated');
      setEditingPresave(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update presave');
    }
  };

  const deletePresave = async (id) => {
    if (!window.confirm('Delete this presave? All signups will be removed.')) return;
    try {
      await axios.delete(`${API_URL}/admin/presaves/${id}`);
      toast.success('Presave deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete presave');
    }
  };

  const releasePresave = async (presave) => {
    if (!window.confirm(`Release "${presave.title}" now? All fans who signed up will be notified.`)) return;
    try {
      await axios.post(`${API_URL}/admin/presaves/${presave.id}/release`);
      toast.success('Released! All fans have been notified.');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release');
    }
  };

  const getDaysUntil = (dateStr) => {
    const release = new Date(dateStr);
    const now = new Date();
    const diff = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Bell className="w-5 h-5 text-pink-400" /> Upcoming Releases
        </h2>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-presave-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Presave
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="glass-card p-6 mb-4">
          <h3 className="text-lg font-semibold mb-4">Create New Presave</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Title *</Label>
              <Input value={newPresave.title} onChange={(e) => setNewPresave({...newPresave, title: e.target.value})} className="input-dark mt-1" placeholder="Single or Album Name" />
            </div>
            <div>
              <Label>Release Date *</Label>
              <Input type="date" value={newPresave.release_date} onChange={(e) => setNewPresave({...newPresave, release_date: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Cover Image</Label>
              <div className="flex gap-2 mt-1">
                <Input value={newPresave.cover_url} onChange={(e) => setNewPresave({...newPresave, cover_url: e.target.value})} className="input-dark flex-1" placeholder="Cover URL or upload image" />
                <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                  <Upload className="w-4 h-4" />
                  {uploading.cover ? 'Uploading...' : 'Upload'}
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleCoverUpload(e, false)} disabled={uploading.cover} />
                </label>
              </div>
              {newPresave.cover_url && <img src={newPresave.cover_url} alt="Cover preview" className="w-16 h-16 rounded mt-2 object-cover" />}
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newPresave.description} onChange={(e) => setNewPresave({...newPresave, description: e.target.value})} className="input-dark mt-1" placeholder="Optional description" />
            </div>
            <div>
              <Label>Spotify Pre-save URL</Label>
              <Input value={newPresave.spotify_url} onChange={(e) => setNewPresave({...newPresave, spotify_url: e.target.value})} className="input-dark mt-1" placeholder="https://open.spotify.com/..." />
            </div>
            <div>
              <Label>Apple Music URL</Label>
              <Input value={newPresave.apple_music_url} onChange={(e) => setNewPresave({...newPresave, apple_music_url: e.target.value})} className="input-dark mt-1" placeholder="https://music.apple.com/..." />
            </div>
            <div className="md:col-span-2">
              <Label>Preview Audio URL (mp3, 30s clip played on-site)</Label>
              <Input value={newPresave.audio_url} onChange={(e) => setNewPresave({...newPresave, audio_url: e.target.value})} className="input-dark mt-1 font-mono text-sm" placeholder="https://.../preview.mp3" />
            </div>
          </div>
          
          {/* Bonus Content Section */}
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg border border-purple-500/20">
            <h4 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
              <Gift className="w-4 h-4" /> Pre-save Bonus Content (Optional)
            </h4>
            <p className="text-sm text-gray-400 mb-4">Reward fans who pre-save with exclusive content!</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Bonus Type</Label>
                <select 
                  value={newPresave.bonus_content_type} 
                  onChange={(e) => setNewPresave({...newPresave, bonus_content_type: e.target.value})} 
                  className="input-dark mt-1 w-full rounded-md"
                >
                  <option value="">No bonus</option>
                  <option value="audio">Bonus Track/Remix</option>
                  <option value="video">Behind-the-Scenes Video</option>
                  <option value="download">Exclusive Download</option>
                  <option value="early_access">Early Access</option>
                </select>
              </div>
              <div>
                <Label>Bonus Content URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={newPresave.bonus_content_url} onChange={(e) => setNewPresave({...newPresave, bonus_content_url: e.target.value})} className="input-dark flex-1" placeholder="URL or upload file" />
                  <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                    <Upload className="w-4 h-4" />
                    {uploading.bonus ? '...' : 'Upload'}
                    <input type="file" className="hidden" accept="audio/*,video/*,.zip,.pdf" onChange={(e) => handleBonusUpload(e, false)} disabled={uploading.bonus} />
                  </label>
                </div>
                {newPresave.bonus_content_url && <p className="text-xs text-green-500 mt-1">✓ Bonus file set</p>}
              </div>
              <div>
                <Label>Bonus Description</Label>
                <Input value={newPresave.bonus_description} onChange={(e) => setNewPresave({...newPresave, bonus_description: e.target.value})} className="input-dark mt-1" placeholder="Exclusive unreleased remix!" />
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={createPresave} className="btn-primary"><Plus className="w-4 h-4 mr-2" /> Create Presave</button>
          </div>
        </div>
      )}

      {/* Presaves List */}
      {presaves.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Bell className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Upcoming Releases</h3>
          <p className="text-gray-400 mb-4">Create a presave to let fans know about upcoming music!</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" /> Add Your First Presave
          </button>
        </div>
      ) : (
        presaves.map(presave => (
          <div key={presave.id} className="glass-card p-4" data-testid={`presave-item-${presave.id}`}>
            {editingPresave?.id === presave.id ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Title</Label><Input value={editingPresave.title} onChange={(e) => setEditingPresave({...editingPresave, title: e.target.value})} className="input-dark mt-1" /></div>
                  <div><Label>Release Date</Label><Input type="date" value={editingPresave.release_date?.split('T')[0]} onChange={(e) => setEditingPresave({...editingPresave, release_date: e.target.value})} className="input-dark mt-1" /></div>
                  <div>
                    <Label>Cover Image</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={editingPresave.cover_url || ''} onChange={(e) => setEditingPresave({...editingPresave, cover_url: e.target.value})} className="input-dark flex-1" placeholder="Cover URL or upload" />
                      <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                        <Upload className="w-4 h-4" />
                        {uploading.cover ? '...' : 'Upload'}
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleCoverUpload(e, true)} disabled={uploading.cover} />
                      </label>
                    </div>
                    {editingPresave.cover_url && <img src={editingPresave.cover_url} alt="Cover" className="w-12 h-12 rounded mt-1 object-cover" />}
                  </div>
                  <div><Label>Description</Label><Input value={editingPresave.description || ''} onChange={(e) => setEditingPresave({...editingPresave, description: e.target.value})} className="input-dark mt-1" /></div>
                  <div><Label>Spotify URL</Label><Input value={editingPresave.spotify_url || ''} onChange={(e) => setEditingPresave({...editingPresave, spotify_url: e.target.value})} className="input-dark mt-1" /></div>
                  <div><Label>Apple Music URL</Label><Input value={editingPresave.apple_music_url || ''} onChange={(e) => setEditingPresave({...editingPresave, apple_music_url: e.target.value})} className="input-dark mt-1" /></div>
                  <div className="md:col-span-2"><Label>Preview Audio URL (30s clip)</Label><Input value={editingPresave.audio_url || ''} onChange={(e) => setEditingPresave({...editingPresave, audio_url: e.target.value})} className="input-dark mt-1 font-mono text-sm" placeholder="https://.../preview.mp3" /></div>
                </div>
                
                {/* Bonus Content Section in Edit Mode */}
                <div className="p-4 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg border border-purple-500/20">
                  <h4 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4" /> Bonus Content for Pre-savers
                  </h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Bonus Type</Label>
                      <select 
                        value={editingPresave.bonus_content_type || ''} 
                        onChange={(e) => setEditingPresave({...editingPresave, bonus_content_type: e.target.value})} 
                        className="input-dark mt-1 w-full rounded-md"
                      >
                        <option value="">No bonus</option>
                        <option value="audio">Bonus Track/Remix</option>
                        <option value="video">Behind-the-Scenes Video</option>
                        <option value="download">Exclusive Download</option>
                        <option value="early_access">Early Access</option>
                      </select>
                    </div>
                    <div>
                      <Label>Bonus Content</Label>
                      <div className="flex gap-2 mt-1">
                        <Input value={editingPresave.bonus_content_url || ''} onChange={(e) => setEditingPresave({...editingPresave, bonus_content_url: e.target.value})} className="input-dark flex-1" placeholder="URL or upload file" />
                        <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                          <Upload className="w-4 h-4" />
                          {uploading.bonus ? '...' : 'Upload'}
                          <input type="file" className="hidden" accept="audio/*,video/*,.zip,.pdf" onChange={(e) => handleBonusUpload(e, true)} disabled={uploading.bonus} />
                        </label>
                      </div>
                      {editingPresave.bonus_content_url && <p className="text-xs text-green-500 mt-1">✓ Bonus file set</p>}
                    </div>
                    <div>
                      <Label>Bonus Description</Label>
                      <Input value={editingPresave.bonus_description || ''} onChange={(e) => setEditingPresave({...editingPresave, bonus_description: e.target.value})} className="input-dark mt-1" placeholder="Exclusive unreleased remix!" />
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingPresave(null)} className="btn-ghost"><X className="w-4 h-4" /> Cancel</button>
                  <button onClick={() => savePresave(editingPresave)} className="btn-primary"><Check className="w-4 h-4" /> Save</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                {presave.cover_url ? (
                  <img src={presave.cover_url} alt={presave.title} className="w-20 h-20 rounded-lg object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                    <Music className="w-8 h-8 text-white" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{presave.title}</h3>
                    {presave.bonus_content_url && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gradient-to-r from-purple-500/30 to-cyan-500/30 text-purple-300 flex items-center gap-1">
                        <Gift className="w-3 h-3" /> Bonus
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">{presave.description || 'No description'}</p>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-xs text-pink-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(presave.release_date).toLocaleDateString()}
                    </span>
                    {presave.signup_count > 0 && (
                      <span className="text-xs text-cyan-400">{presave.signup_count} pre-saves</span>
                    )}
                  </div>
                </div>
                <div className="text-right mr-4">
                  {presave.is_released ? (
                    <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm">Released</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-pink-500/20 text-pink-400 text-sm">
                      {getDaysUntil(presave.release_date) > 0 ? `${getDaysUntil(presave.release_date)} days` : 'Ready to drop!'}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {!presave.is_released && (
                    <button onClick={() => releasePresave(presave)} className="btn-ghost text-green-400 flex items-center gap-1" title="Release now & notify fans">
                      <Rocket className="w-4 h-4" /> Release
                    </button>
                  )}
                  <button onClick={() => setEditingPresave({...presave})} className="btn-ghost">Edit</button>
                  <button onClick={() => deletePresave(presave.id)} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
